import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Paper,
  Avatar,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
  useMediaQuery,
  Collapse,
  Alert
} from '@mui/material';
import { 
  HowToReg, 
  Visibility, 
  VisibilityOff,
  FaceRetouchingNatural,
  Email,
  Password,
  RocketLaunch,
  CheckCircle
} from '@mui/icons-material';
import { styled, alpha, useTheme } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { useSnackbar } from 'notistack';
import validator from 'validator';
import useAnalytics from '../../hooks/useAnalytics';

// Enhanced styled components - consistent with Login.js
const QuantumCard = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.92)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`,
  backdropFilter: 'blur(16px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: `
    0 8px 32px 0 ${alpha(theme.palette.primary.main, 0.18)},
    inset 0 0 12px ${alpha(theme.palette.primary.light, 0.1)}
  `,
  borderRadius: '28px',
  overflow: 'hidden',
  position: 'relative',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '6px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    animation: 'rainbow 8s linear infinite',
    backgroundSize: '400% 400%'
  },
  '@keyframes rainbow': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' }
  }
}));

const RegisterButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.85)} 0%, ${alpha(theme.palette.secondary.main, 0.7)} 100%)`,
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.15)',
  boxShadow: `
    0 4px 30px ${alpha(theme.palette.primary.main, 0.25)},
    inset 0 2px 1px ${alpha(theme.palette.common.white, 0.2)}
  `,
  color: theme.palette.common.white,
  borderRadius: '18px',
  padding: '14px 36px',
  fontSize: '1.05rem',
  fontWeight: 600,
  letterSpacing: '0.06em',
  transition: 'all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
  textTransform: 'none',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: `
      0 12px 45px ${alpha(theme.palette.primary.main, 0.4)},
      inset 0 2px 1px ${alpha(theme.palette.common.white, 0.3)}
    `,
    background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.95)} 0%, ${alpha(theme.palette.secondary.main, 0.8)} 100%)`,
  },
  '&.Mui-disabled': {
    background: `linear-gradient(145deg, ${alpha(theme.palette.action.disabledBackground, 0.8)} 0%, ${alpha(theme.palette.action.disabledBackground, 0.6)} 100%)`,
    color: alpha(theme.palette.text.disabled, 0.8)
  }
}));

const AnimatedInput = styled(TextField)(({ theme, error }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '14px',
    transition: 'all 0.3s ease',
    '& fieldset': {
      borderColor: error ? theme.palette.error.main : alpha(theme.palette.primary.main, 0.25),
      transition: 'all 0.3s ease'
    },
    '&:hover fieldset': {
      borderColor: error ? theme.palette.error.main : alpha(theme.palette.primary.main, 0.6),
      boxShadow: `0 0 0 3px ${alpha(error ? theme.palette.error.main : theme.palette.primary.main, 0.15)}`
    },
    '&.Mui-focused fieldset': {
      borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
      boxShadow: `0 0 0 3px ${alpha(error ? theme.palette.error.main : theme.palette.primary.main, 0.25)}`,
      borderWidth: '2px'
    }
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: theme.palette.primary.main,
    fontWeight: 500
  }
}));

// Enhanced error mapping function
const mapRegistrationError = (error) => {
  const status = error.response?.status;
  const data = error.response?.data;
  
  console.log('Registration error details:', { status, data, error: error.message });

  // Handle specific error messages from server
  if (data?.message) {
    const message = data.message.toLowerCase();
    
    if (message.includes('already exists') || message.includes('duplicate') || message.includes('user exist')) {
      return {
        field: 'email',
        message: 'Email already registered',
        userMessage: 'This email is already registered. Please try logging in instead.'
      };
    }
    
    if (message.includes('email') && message.includes('invalid')) {
      return {
        field: 'email',
        message: 'Invalid email address',
        userMessage: 'Please enter a valid email address.'
      };
    }
    
    if (message.includes('password') || message.includes('weak')) {
      return {
        field: 'password',
        message: 'Password requirements not met',
        userMessage: 'Password must be at least 8 characters with uppercase letters and numbers.'
      };
    }
    
    if (message.includes('name') || message.includes('username')) {
      return {
        field: 'name',
        message: 'Invalid name',
        userMessage: 'Please enter a valid name.'
      };
    }
  }

  // Handle by status code
  switch (status) {
    case 400:
      return {
        field: 'general',
        message: 'Invalid registration data',
        userMessage: 'Please check your information and try again.'
      };
    case 409:
      return {
        field: 'email',
        message: 'Email already registered',
        userMessage: 'This email is already registered. Please try logging in instead.'
      };
    case 422:
      return {
        field: 'general',
        message: 'Validation failed',
        userMessage: 'Please check all fields and try again.'
      };
    case 500:
      return {
        field: 'general',
        message: 'Server error',
        userMessage: 'Server is temporarily unavailable. Please try again later.'
      };
    default:
      return {
        field: 'general',
        message: error.message || 'Registration failed',
        userMessage: 'Registration failed. Please check your connection and try again.'
      };
  }
};

export default function Register() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState(null);
  
  const { register, login } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const { trackEvent, trackUserRegistration } = useAnalytics();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!validator.isEmail(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/[A-Z]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter';
    } else if (!/[0-9]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one number';
    } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one special character';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear general error
    if (error) {
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Use try-catch to handle analytics errors gracefully
    try {
      trackEvent('RegistrationInitiated');
    } catch (analyticsError) {
      console.warn('Analytics error (non-blocking):', analyticsError);
    }

    if (!validateForm()) {
      enqueueSnackbar('Please fix the form errors', { variant: 'error' });
      return;
    }

    setLoading(true);
    setActiveStep(1);
    setError(null);

    try {
      // Call register from AuthContext
      const registerResult = await register({
        name: formData.name.trim(),
        email: formData.email.toLowerCase().trim(),
        password: formData.password
      });

      if (registerResult) {
        // Try to track registration, but don't let it block the flow
        try {
          trackUserRegistration({
            name: formData.name.trim(),
            email: formData.email.toLowerCase().trim()
          }, 'email');
        } catch (analyticsError) {
          console.warn('Analytics error (non-blocking):', analyticsError);
        }

        // After successful registration, automatically log the user in
        try {
          await login({
            email: formData.email.toLowerCase().trim(),
            password: formData.password
          });
          
          // Success flow
          setActiveStep(2);
          await new Promise(resolve => setTimeout(resolve, 800));
          
          enqueueSnackbar('Registration successful!', { variant: 'success' });
          
          setActiveStep(3);
          setTimeout(() => navigate('/dashboard'), 1000);
        } catch (loginError) {
          // Registration succeeded but login failed
          enqueueSnackbar('Registration successful! Please log in.', { variant: 'success' });
          setTimeout(() => navigate('/login'), 1000);
        }
      }
      
    } catch (error) {
      setActiveStep(0);
      setLoading(false);
      
      // Enhanced error handling with mapping
      const mappedError = mapRegistrationError(error);
      
      console.log('Mapped registration error:', mappedError);
      
      // Set field-specific error
      if (mappedError.field && mappedError.field !== 'general') {
        setErrors({ [mappedError.field]: mappedError.message });
      }
      
      // Set general error for display
      setError(mappedError.userMessage);
      
      // Show appropriate snackbar message
      enqueueSnackbar(mappedError.userMessage, { 
        variant: mappedError.field === 'email' ? 'warning' : 'error',
        autoHideDuration: 6000 
      });
      
      // Try to track failure, but don't let it cause issues
      try {
        trackEvent('RegistrationFailed', { 
          error: mappedError.message,
          field: mappedError.field
        });
      } catch (analyticsError) {
        console.warn('Analytics error (non-blocking):', analyticsError);
      }
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 2,
        background: `linear-gradient(
          135deg, 
          ${theme.palette.background.default} 0%, 
          ${alpha(theme.palette.primary.dark, 0.15)} 100%
        )`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <QuantumCard elevation={3}>
        <Box sx={{ 
          p: isMobile ? 3 : 4,
          textAlign: 'center',
          maxWidth: '500px',
          width: '100%'
        }}>
          {/* Progress Indicators */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: 1.5, 
            mb: 4 
          }}>
            {[0, 1, 2, 3].map((step) => (
              <Box
                key={step}
                sx={{
                  width: '14px',
                  height: '14px',
                  borderRadius: '50%',
                  bgcolor: activeStep >= step ? 
                    theme.palette.primary.main : 
                    alpha(theme.palette.action.disabled, 0.2),
                  transition: 'all 0.4s ease',
                  boxShadow: activeStep >= step ? 
                    `0 0 8px ${alpha(theme.palette.primary.main, 0.5)}` : 
                    'none'
                }}
              />
            ))}
          </Box>
          
          <Avatar sx={{ 
            m: '0 auto 20px',
            bgcolor: 'primary.main',
            width: 80,
            height: 80,
            boxShadow: `0 0 0 6px ${alpha(theme.palette.primary.main, 0.2)}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'rotate(15deg) scale(1.1)',
              boxShadow: `0 0 0 8px ${alpha(theme.palette.primary.main, 0.3)}`
            }
          }}>
            <HowToReg sx={{ fontSize: 40 }} />
          </Avatar>
          
          <Typography component="h1" variant="h3" sx={{ 
            mb: 1,
            fontWeight: 800,
            background: `linear-gradient(
              45deg, 
              ${theme.palette.primary.main} 30%, 
              ${theme.palette.secondary.main} 90%
            )`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '0.05em',
            lineHeight: 1.2
          }}>
            Create Account
          </Typography>
          
          <Typography variant="subtitle1" color="text.secondary" sx={{ 
            mb: 4,
            maxWidth: '80%',
            mx: 'auto'
          }}>
            {activeStep === 0 
              ? "Join our platform and get started today" 
              : activeStep === 1 
                ? "Creating your account..." 
                : activeStep === 2 
                  ? "Finalizing setup..." 
                  : "Registration complete! Redirecting..."}
          </Typography>
          
          <AnimatePresence mode='wait'>
            {activeStep === 0 ? (
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                  <Collapse in={!!error}>
                    <Alert 
                      severity="error" 
                      sx={{ mb: 2 }}
                      action={
                        error?.includes('already registered') ? (
                          <Button 
                            color="inherit" 
                            size="small"
                            onClick={() => navigate('/login')}
                          >
                            Sign In
                          </Button>
                        ) : null
                      }
                    >
                      {error}
                    </Alert>
                  </Collapse>
                  
                  <AnimatedInput
                    margin="normal"
                    required
                    fullWidth
                    name="name"
                    label="Full Name"
                    autoComplete="name"
                    autoFocus
                    value={formData.name}
                    onChange={handleChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FaceRetouchingNatural color={errors.name ? 'error' : 'primary'} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                  
                  <AnimatedInput
                    margin="normal"
                    required
                    fullWidth
                    name="email"
                    label="Email Address"
                    type="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!errors.email}
                    helperText={errors.email}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color={errors.email ? 'error' : 'primary'} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                  
                  <AnimatedInput
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={handleChange}
                    error={!!errors.password}
                    helperText={errors.password}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Password color={errors.password ? 'error' : 'primary'} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                  
                  <AnimatedInput
                    margin="normal"
                    required
                    fullWidth
                    name="confirmPassword"
                    label="Confirm Password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Password color={errors.confirmPassword ? 'error' : 'primary'} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 3 }}
                  />
                  
                  <RegisterButton
                    type="submit"
                    fullWidth
                    size="large"
                    disabled={loading}
                    sx={{ height: 52, mb: 2 }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      <>
                        <RocketLaunch sx={{ mr: 1 }} />
                        Create Account
                      </>
                    )}
                  </RegisterButton>
                  
                  <Divider sx={{ 
                    my: 2, 
                    opacity: 0.3,
                    borderColor: 'divider'
                  }} />
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Already have an account?{' '}
                    <Link 
                      href="#" 
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/login');
                      }}
                      sx={{ 
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        '&:hover': { color: theme.palette.primary.main }
                      }}
                    >
                      Sign In
                    </Link>
                  </Typography>
                </Box>
              </motion.div>
            ) : activeStep === 1 ? (
              <motion.div
                key="processing"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                style={{ textAlign: 'center', padding: '40px 0' }}
              >
                <CircularProgress size={80} thickness={4} color="primary" sx={{ mb: 3 }} />
                <Typography variant="h5" sx={{ mb: 1 }}>
                  Creating Your Account
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Securing your information...
                </Typography>
              </motion.div>
            ) : activeStep === 2 ? (
              <motion.div
                key="security"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                style={{ textAlign: 'center', padding: '40px 0' }}
              >
                <CheckCircle color="primary" sx={{ fontSize: 80, mb: 3 }} />
                <Typography variant="h5" sx={{ mb: 1 }}>
                  Account Created!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Finalizing your setup...
                </Typography>
              </motion.div>
            ) : (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                style={{ textAlign: 'center', padding: '40px 0' }}
              >
                <CheckCircle color="success" sx={{ fontSize: 80, mb: 3 }} />
                <Typography variant="h5" sx={{ mb: 1 }}>
                  Registration Complete!
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Redirecting to your dashboard...
                </Typography>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </QuantumCard>
    </Box>
  );
}